import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import * as DateHelpers from './DateHelpers';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';

interface JobInfoProps {
    systemCompanyId: number;
    scheduleId: number;
    showModal: boolean;
    openGearlist(): void;
    hideModal(): void;
    saveModal(): void;
}

interface InfoDetails {
    scheduleId: number;
    itemTypeId: number;
    siteAddress: string;
    bookedBy: string;
    bookedDate: string;
    quoteNumber: string;
    quotedPriceFormatted: string;
    daysHire: number;
    type: string;
    purchaseOrderNumber: string;
    customer: string;
    customerAccountType: string;
    customerContactId: number;
    customerCashSaleAccount: boolean;
    customerContactName: string;
    customerContactPhone: string;
    customerContactEmail: string;
    contact: string;
    siteContact: string;
    siteContact2: string;
    status: string;
    planChanges: boolean;
    bringForwardJob: boolean;
    bringForwardDate: string;
    jobConfirmed: boolean;
    emailSent: boolean;
    unitNumber: string;
    certificateNumber: string;
    showEquipment: boolean;
    equipment: string;
}

const initJobInfo: InfoDetails = {
    scheduleId: 0,
    itemTypeId: 0,
    siteAddress: "",
    bookedBy: "",
    bookedDate: "",
    quoteNumber: "",
    quotedPriceFormatted: "$0.00",
    daysHire: 0,
    type: "",
    purchaseOrderNumber: "",
    customer: "",
    customerContactId: -1,
    customerCashSaleAccount: false,
    customerContactName: "",
    customerContactPhone: "",
    customerContactEmail: "",
    customerAccountType: "",
    contact: "",
    siteContact: "",
    siteContact2: "",
    status: "",
    planChanges: false,
    bringForwardJob: false,
    bringForwardDate: "",
    jobConfirmed: false,
    emailSent: false,
    unitNumber: "",
    certificateNumber: "",
    showEquipment: false,
    equipment: ""
}

interface GenericList {
    id: number;
    name: string;
}

const JobInfoData = (props: JobInfoProps) => {
    const [infoDetails, setInfoDetails] = React.useState<InfoDetails>(initJobInfo);
    const [customerContactOptions, setCustomerContactOptions] = React.useState<GenericList[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    React.useEffect(() => {
        if (props.scheduleId > 0 && props.showModal) {
            getData();
        }
    }, [props.scheduleId]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/GetJobInfo?ScheduleId=' + props.scheduleId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                setInfoDetails(res.data.jobInfo);
                setCustomerContactOptions(res.data.customerContactOptions);
                setLoading(false);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const hideModal = () => {
        props.hideModal();
    }

    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }

        setInfoDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleCheckboxChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }

        setInfoDetails(prevState => ({ ...prevState, [e.target.name]: e.target.checked }));
    }

    const handleBringForwardCheckboxChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }

        setInfoDetails(prevState => ({ ...prevState, [e.target.name]: e.target.checked }));

        if (!e.target.checked) {
            //need to clear the bringforward date
            setInfoDetails(prevState => ({ ...prevState, ["bringForwardDate"]: "" }));
        }
    }

    const openGearlist = () => {
        props.openGearlist();
    }

    const validate = () => {
        let error: any = {};
        let formIsValid = true;

        if (infoDetails.bringForwardJob && !infoDetails.bringForwardDate) {
            error["bringForwardDate"] = "Bring Forward Date is required";
            formIsValid = false;
        } else if (infoDetails.bringForwardJob && infoDetails.bringForwardDate && !DateHelpers.isValidDate(infoDetails.bringForwardDate)) {
            error["bringForwardDate"] = "Valid Bring Forward Date is required";
            formIsValid = false;
        }

        if (infoDetails.customerCashSaleAccount) {
            if (!infoDetails.customerContactName) {
                error["customerContactName"] = "Customer Contact Name is required";
                formIsValid = false;
            }
            if (!infoDetails.customerContactPhone) {
                error["customerContactPhone"] = "Customer Contact Phone is required";
                formIsValid = false;
            }
            if (!infoDetails.customerContactEmail) {
                error["customerContactEmail"] = "Customer Contact Email is required";
                formIsValid = false;
            }
        }

        setErrors(error);
        return formIsValid;
    }

    const save = () => {
        if (validate()) {
            saveInfo();
        }
    }

    const saveInfo = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var saveJobInfo = {
            jobInfo: infoDetails,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveJobInfo', saveJobInfo, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                setInfoDetails(initJobInfo);
                props.saveModal();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const renderDetails = (
        <div className="static-modal">
            <Modal show={props.showModal} onHide={hideModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>More Info <br></br> {infoDetails.siteAddress}</Modal.Title>
                    <button type="button" className="close" onClick={hideModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <label className="input-group" htmlFor="bookedBy">
                        <span className="label">Booked By</span>
                        <input className="input" type="text" id="bookedBy" name="bookedBy" value={infoDetails.bookedBy} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="bookedDate">
                        <span className="label">Booked Date</span>
                        <input className="input" type="text" id="bookedDate" name="bookedDate" value={infoDetails.bookedDate} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="quoteNumber">
                        <span className="label">Quote No</span>
                        <input className="input" type="text" id="quoteNumber" name="quoteNumber" value={infoDetails.quoteNumber} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="quotedPriceFormatted">
                        <span className="label">Quoted Price</span>
                        <input className="input" type="text" id="quotedPriceFormatted" name="quotedPriceFormatted" value={infoDetails.quotedPriceFormatted} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="daysHire">
                        <span className="label">Days Hire</span>
                        <input className="input" type="text" id="daysHire" name="daysHire" value={infoDetails.daysHire} disabled></input>
                    </label>
                    <label className="input-group" htmlFor="type">
                        <span className="label">Type</span>
                        <input className="input" type="text" id="type" name="type" value={infoDetails.type} disabled></input>
                    </label>
                    <div className={props.systemCompanyId === 2 ? "hidden" : ""}>
                        <label className="input-group" htmlFor="unitNumber">
                            <span className="label">{infoDetails.itemTypeId === 59 ? "Waste Log Number" : "Unit Number (SKU)"}</span>
                            <input className="input" type="text" maxLength={100} id="unitNumber" name="unitNumber" value={infoDetails.unitNumber} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <label className={infoDetails.itemTypeId === 59 || infoDetails.itemTypeId === 61 ? "input-group" : "hidden"} htmlFor="certificateNumber">
                            <span className="label">Waste Certificate Number</span>
                            <input className="input" type="text" maxLength={100} id="certificateNumber" name="certificateNumber" value={infoDetails.certificateNumber} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <label className={infoDetails.showEquipment ? "input-group" : "hidden"} htmlFor="equipment">
                            <span className="label">Equipment</span>
                            <input className="input" type="text" id="equipment" name="equipment" value={infoDetails.equipment} disabled></input>
                        </label>
                    </div>
                    <label className="input-group" htmlFor="purchaseOrderNumber">
                        <span className="label">PO Number</span>
                        <input className="input" type="text" maxLength={25} id="purchaseOrderNumber" name="purchaseOrderNumber" value={infoDetails.purchaseOrderNumber} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <label className="input-group" htmlFor="customer">
                        <span className="label">Customer</span>
                        <input className="input" type="text" id="customer" name="customer" value={infoDetails.customer} disabled></input>
                    </label>
                    <label className={infoDetails.customerCashSaleAccount ? "hidden" : "input-group"} htmlFor="customerContactId">
                        <span className="label">Contact</span>
                        <select className="select schedulenew__contact-size" id="customerContactId" name="customerContactId" value={infoDetails.customerContactId} onChange={(e) => handleChange(e)}>
                            <option hidden defaultValue="-1"></option>
                            {customerContactOptions.map(custcon =>
                                <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                            )};
                        </select>
                    </label>
                    <div className={infoDetails.customerCashSaleAccount ? "" : "hidden"}>
                        <label className="input-group" htmlFor="customerContactName">
                            <span className="label">Customer Contact Name</span>
                            <input className="input" type="text" maxLength={50} id="customerContactName" name="customerContactName" value={infoDetails.customerContactName} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactName"] ? "label marginLeftLabel errors" : "hidden"}>{errors["customerContactName"]}</span>
                        <label className="input-group" htmlFor="customerContactPhone">
                            <span className="label">Customer Contact Phone</span>
                            <input className="input" type="text" maxLength={30} id="customerContactPhone" name="customerContactPhone" value={infoDetails.customerContactPhone} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactPhone"] ? "label marginLeftLabel errors" : "hidden"}>{errors["customerContactPhone"]}</span>
                        <label className="input-group" htmlFor="customerContactEmail">
                            <span className="label">Customer Contact Email</span>
                            <input className="input" type="text" maxLength={200} id="customerContactEmail" name="customerContactEmail" value={infoDetails.customerContactEmail} onChange={(e) => handleChange(e)}></input>
                        </label>
                        <span className={errors["customerContactEmail"] ? "label marginLeftLabel errors" : "hidden"}>{errors["customerContactEmail"]}</span>
                    </div>
                    <label className="input-group" htmlFor="customerAccountType">
                        <span className="label">Customer Account Type</span>
                        <input className="input" type="text" id="customerAccountType" name="customerAccountType" value={infoDetails.customerAccountType} disabled></input>
                    </label>                       
                    <label className="input-group" htmlFor="siteContact">
                        <span className="label">Site Contact</span>
                        <input className="input" type="text" maxLength={500} id="siteContact" name="siteContact" value={infoDetails.siteContact} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <label className="input-group" htmlFor="siteContact2">
                        <span className="label">Site Contact 2</span>
                        <input className="input" type="text" maxLength={500} id="siteContact2" name="siteContact2" value={infoDetails.siteContact2} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <label className="input-group" htmlFor="bringForwardJob">
                        <span className="label">Bring forward the job?</span>
                        <input className='checkbox' type="checkbox" name="bringForwardJob" id="bringForwardJob" checked={infoDetails.bringForwardJob} onChange={(e) => handleBringForwardCheckboxChange(e)}></input>
                    </label>
                    <label className={infoDetails.bringForwardJob ? "input-group" : "hidden"} htmlFor="bringForwardDate">
                        <span className="label">Bring Forward Date</span>
                        <input className="input" type="date" id="bringForwardDate" name="bringForwardDate" value={infoDetails.bringForwardDate} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["bringForwardDate"] ? "label errors" : "hidden"}>{errors["bringForwardDate"]}</span>

                    <div className={props.systemCompanyId === 2 ? "" : "hidden" }>
                        <label className="input-group" htmlFor="planChanges">
                            <span className="label">Any plan changes?</span>
                            <input className='checkbox' type="checkbox" name="planChanges" id="planChanges" checked={infoDetails.planChanges} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                        <label className="input-group" htmlFor="jobConfirmed">
                            <span className="label">Gear List Done?</span>
                            <input className='checkbox' type="checkbox" name="jobConfirmed" id="jobConfirmed" checked={infoDetails.jobConfirmed} onChange={(e) => handleCheckboxChange(e)}></input>
                        </label>
                    </div>
                    <label className="input-group" htmlFor="emailSent">
                        <span className="label">Email Sent?</span>
                        <input className='checkbox' type="checkbox" name="emailSent" id="emailSent" checked={infoDetails.emailSent} onChange={(e) => handleCheckboxChange(e)} disabled={props.systemCompanyId === 3}></input>
                    </label>
                </Modal.Body>
                <Modal.Footer>
                    <button className={infoDetails.itemTypeId === 9 ? "popup-button--no" : "hidden"} onClick={openGearlist}>Gear List</button>
                    <button className="popup-button--yes" onClick={save}>Save</button>
                </Modal.Footer>
            </Modal>
        </div>
    );

    let contents =
        loading || !props.showModal ? ""
        : renderDetails;

    return (<div>
        {contents}
    </div>)
}

export default JobInfoData;